import React, { useState, useMemo } from 'react';
import { Search, Loader2, ExternalLink, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { amplifyApi } from '../services/amplifyApi';
import { useBrand } from '../context/BrandContext';

const PAGE_SIZE = 20;

// Default source configuration - used as fallback if brand config doesn't specify
const DEFAULT_LIBRARY_SOURCES = [
  { id: 'world-illustrated', name: 'World Illustrated', enabled: true },
  { id: 'rawpixel', name: 'Rawpixel', enabled: true },
  { id: 'met', name: 'The Met', enabled: false },
];

// World Illustrated channel categories (from their API - getAllChannels query)
const WORLD_ILLUSTRATED_CHANNELS = [
  { id: 33, name: 'Our Planet' },
  { id: 34, name: 'Arts & Culture' },
  { id: 35, name: 'Design' },
  { id: 36, name: 'Music' },
  { id: 37, name: 'Hobbies' },
  { id: 38, name: 'Tech & Science' },
  { id: 39, name: 'Famous' },
  { id: 40, name: 'Sport' },
  { id: 41, name: 'Health' },
  { id: 42, name: 'Food & Drink' },
  { id: 43, name: 'Transport' },
  { id: 45, name: 'Junior' },
  { id: 46, name: 'Community' },
  { id: 47, name: 'Military' },
  { id: 48, name: 'News' },
  { id: 69, name: 'Fashion' },
];

const LibraryPicker = ({ onSelectImage }) => {
  const brand = useBrand();

  // Get library sources from brand config, fallback to defaults
  const librarySources = useMemo(() => {
    return brand.features?.artLibrary?.sources || DEFAULT_LIBRARY_SOURCES;
  }, [brand.features?.artLibrary?.sources]);

  const enabledSources = useMemo(() => {
    return librarySources.filter(s => s.enabled);
  }, [librarySources]);

  const [activeSource, setActiveSource] = useState(enabledSources[0]?.id || 'met');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // For page-based APIs like Rawpixel
  const [lastQuery, setLastQuery] = useState('');
  const [activeChannel, setActiveChannel] = useState(null); // For World Illustrated channel filtering { id, name }
  const [lastChannel, setLastChannel] = useState(null); // Track channel used in last search
  const [fetchingHighRes, setFetchingHighRes] = useState(null); // Track which artwork is being fetched for high-res

  // Search dispatcher - routes to correct API based on active source
  const searchBySource = async (sourceId, searchQuery, limit, offset, page, channel = null) => {
    switch (sourceId) {
      case 'met':
        return amplifyApi.searchMetArt(searchQuery, limit, offset);
      case 'rawpixel':
        return amplifyApi.searchRawpixel(searchQuery, limit, page);
      case 'world-illustrated':
        return amplifyApi.searchWorldIllustrated(searchQuery, limit, page, channel?.id, channel?.name);
      default:
        return { success: false, artworks: [], error: 'Unknown source' };
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setCurrentOffset(0);
    setCurrentPage(1);
    setLastQuery(query.trim());
    setLastChannel(activeChannel);

    const response = await searchBySource(activeSource, query.trim(), PAGE_SIZE, 0, 1, activeChannel);

    if (response.success) {
      setResults(response.artworks);
      setTotalResults(response.total);
      setHasMore(response.hasMore);
      setCurrentOffset(PAGE_SIZE);
      setCurrentPage(2); // Next page for Rawpixel
    } else {
      setError(response.error || 'Search failed');
      setResults([]);
      setHasMore(false);
    }

    setSearched(true);
    setLoading(false);
  };

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);

    const response = await searchBySource(activeSource, lastQuery, PAGE_SIZE, currentOffset, currentPage, lastChannel);

    if (response.success) {
      setResults(prev => [...prev, ...response.artworks]);
      setHasMore(response.hasMore);
      setCurrentOffset(prev => prev + PAGE_SIZE);
      setCurrentPage(prev => prev + 1);
    } else {
      setError(response.error || 'Failed to load more');
    }

    setLoadingMore(false);
  };

  const handleSelect = async (artwork) => {
    // For World Illustrated, fetch the signed URL for high-res access
    if (artwork.source === 'world-illustrated' && artwork.contentId) {
      setFetchingHighRes(artwork.id);
      setError(null);

      try {
        const signedUrlResponse = await amplifyApi.getWorldIllustratedSignedUrl(artwork.contentId);

        if (signedUrlResponse.success && signedUrlResponse.signedURL) {
          onSelectImage({
            url: signedUrlResponse.signedURL,  // High-res signed URL
            name: artwork.title,
            source: artwork.source,
            attribution: artwork.attribution,
            isPublicDomain: artwork.isPublicDomain
          });
        } else {
          // Failed to get signed URL - show error but don't fall back
          setError(`Failed to get high-resolution image: ${signedUrlResponse.error || 'Unknown error'}`);
        }
      } catch (err) {
        setError(`Failed to get high-resolution image: ${err.message}`);
      } finally {
        setFetchingHighRes(null);
      }
      return;
    }

    // For other sources, use the URL directly
    onSelectImage({
      url: artwork.imageUrl,
      urlFallbacks: artwork.imageUrlFallbacks || [],  // Array of fallback URLs for Rawpixel (2000px, 1000px, 800px)
      name: artwork.title,
      source: artwork.source,
      attribution: artwork.attribution,
      isPublicDomain: artwork.isPublicDomain
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  // Handle quick search - for World Illustrated, channel object can be passed for category filtering
  const handleQuickSearch = (term, channel = null) => {
    setQuery(term);
    if (channel) {
      setActiveChannel(channel);
    }
    // Use a ref-like pattern to search with the new term
    setTimeout(async () => {
      setLoading(true);
      setError(null);
      setCurrentOffset(0);
      setCurrentPage(1);
      setLastQuery(term);
      setLastChannel(channel);

      const response = await searchBySource(activeSource, term, PAGE_SIZE, 0, 1, channel);

      if (response.success) {
        setResults(response.artworks);
        setTotalResults(response.total);
        setHasMore(response.hasMore);
        setCurrentOffset(PAGE_SIZE);
        setCurrentPage(2);
      } else {
        setError(response.error || 'Search failed');
        setResults([]);
        setHasMore(false);
      }

      setSearched(true);
      setLoading(false);
    }, 0);
  };

  const handleSourceChange = (sourceId) => {
    setActiveSource(sourceId);
    setResults([]);
    setSearched(false);
    setQuery('');
    setError(null);
    setCurrentOffset(0);
    setCurrentPage(1);
    setActiveChannel(null);
    setLastChannel(null);
  };

  // Get description based on active source
  const getSourceDescription = () => {
    switch (activeSource) {
      case 'met':
        return 'Search millions of artworks from The Metropolitan Museum of Art';
      case 'rawpixel':
        return 'Search public domain photos, illustrations, and vectors from Rawpixel';
      case 'world-illustrated':
        return 'Search photos and illustrations from World Illustrated';
      default:
        return 'Search public domain artworks';
    }
  };

  // Get quick search terms based on active source
  const getQuickSearchTerms = () => {
    switch (activeSource) {
      case 'met':
        return ['Van Gogh', 'Monet', 'Japanese Art', 'Egyptian', 'Renaissance', 'Abstract'];
      case 'rawpixel':
        return ['Flowers', 'Vintage', 'Nature', 'Pattern', 'Animals', 'Botanical'];
      default:
        return ['Art', 'Nature', 'Abstract', 'Vintage'];
    }
  };

  // Get attribution text based on active source
  const getAttributionText = () => {
    switch (activeSource) {
      case 'met':
        return 'Powered by The Metropolitan Museum of Art Open Access API';
      case 'rawpixel':
        return 'Powered by Rawpixel - CC0 Public Domain';
      case 'world-illustrated':
        return 'Powered by World Illustrated';
      default:
        return 'Public Domain Images';
    }
  };

  return (
    <div className="space-y-4">
      {/* Source Selector - only show if 2+ sources enabled */}
      {enabledSources.length > 1 && (
        <div className="flex gap-2 justify-center mb-2">
          {enabledSources.map(source => (
            <button
              key={source.id}
              onClick={() => handleSourceChange(source.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeSource === source.id
                  ? 'text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              style={activeSource === source.id ? { backgroundColor: brand.colors.primary } : {}}
            >
              {source.name}
            </button>
          ))}
        </div>
      )}

      {/* Search Header */}
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-slate-800">
          {activeSource === 'world-illustrated' ? 'Curated Pro Imagery' : 'Public Domain Art Library'}
        </h3>
        <p className="text-sm text-slate-500">
          {getSourceDescription()}
        </p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              activeSource === 'rawpixel' ? "Search images (e.g., flowers, vintage, nature)" :
              activeSource === 'world-illustrated' ? (
                activeChannel
                  ? `Search within ${activeChannel.name}...`
                  : "Search photos or choose a category below"
              ) :
              "Search artworks (e.g., van gogh, monet, japanese)"
            }
            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg transition-colors outline-none"
            onFocus={(e) => {
              e.target.style.borderColor = brand.colors.primary;
              e.target.style.boxShadow = `0 0 0 3px ${brand.colors.primary}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '';
              e.target.style.boxShadow = '';
            }}
          />
        </div>
        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="px-6 py-3 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          style={{ backgroundColor: brand.colors.primary }}
          onMouseEnter={(e) => { if (!e.target.disabled) e.target.style.backgroundColor = brand.colors.primaryHover || brand.colors.primary; }}
          onMouseLeave={(e) => { e.target.style.backgroundColor = brand.colors.primary; }}
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search size={18} />
              Search
            </>
          )}
        </button>
      </form>

      {/* World Illustrated Category Buttons - Always visible */}
      {activeSource === 'world-illustrated' && (
        <div className="bg-slate-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-slate-700 text-sm">Browse by Category</h4>
            {activeChannel && (
              <button
                onClick={() => {
                  setActiveChannel(null);
                  setQuery('');
                  setResults([]);
                  setSearched(false);
                }}
                className="text-slate-400 hover:text-slate-600 underline text-xs"
              >
                Clear filter
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {WORLD_ILLUSTRATED_CHANNELS.map((channel) => (
              <button
                key={channel.id}
                onClick={() => handleQuickSearch('*', channel)}
                disabled={loading}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  activeChannel?.id === channel.id
                    ? 'text-white'
                    : 'bg-white border border-slate-200 text-slate-600'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                style={activeChannel?.id === channel.id
                  ? { backgroundColor: brand.colors.primary }
                  : {}
                }
                onMouseEnter={(e) => {
                  if (activeChannel?.id !== channel.id) {
                    e.target.style.backgroundColor = `${brand.colors.primary}10`;
                    e.target.style.borderColor = `${brand.colors.primary}60`;
                    e.target.style.color = brand.colors.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeChannel?.id !== channel.id) {
                    e.target.style.backgroundColor = '';
                    e.target.style.borderColor = '';
                    e.target.style.color = '';
                  }
                }}
              >
                {channel.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Results Grid */}
      {results.length > 0 && (
        <div>
          <p className="text-sm text-slate-500 mb-3">
            Showing {results.length} of {totalResults.toLocaleString()} results
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {results.map((artwork) => {
              const isFetchingThis = fetchingHighRes === artwork.id;
              return (
              <div
                key={artwork.id}
                onClick={() => !isFetchingThis && handleSelect(artwork)}
                className={`group cursor-pointer bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-all ${isFetchingThis ? 'opacity-75 cursor-wait' : ''}`}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${brand.colors.primary}80`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-slate-100 relative overflow-hidden">
                  <img
                    src={artwork.thumbnailUrl}
                    alt={artwork.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  {/* Loading Overlay - shown when fetching high-res */}
                  {isFetchingThis && (
                    <div className="absolute inset-0 bg-slate-900/70 flex flex-col items-center justify-center">
                      <Loader2 size={24} className="animate-spin text-white mb-2" />
                      <span className="text-white text-xs font-medium">Loading high-res...</span>
                    </div>
                  )}
                  {/* Hover Overlay - only show when not fetching */}
                  {!isFetchingThis && (
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      style={{ backgroundColor: `${brand.colors.primary}cc` }}
                    >
                      <span className="text-white font-medium text-sm">Select</span>
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="p-2">
                  <h4 className="text-xs font-medium text-slate-800 line-clamp-2 leading-tight">
                    {artwork.title}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    {artwork.artist}
                  </p>
                  {artwork.date && (
                    <p className="text-xs text-slate-400 truncate">
                      {artwork.date}
                    </p>
                  )}
                </div>
              </div>
            );
            })}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loadingMore ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ChevronDown size={18} />
                    Load More
                  </>
                )}
              </button>
              {totalResults > results.length && (
                <p className="text-xs text-slate-400 mt-2">
                  {totalResults - results.length} more results available
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {searched && results.length === 0 && !error && !loading && (
        <div className="text-center py-12">
          <ImageIcon size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No artworks found for "{lastQuery}"</p>
          <p className="text-sm text-slate-400 mt-1">
            Try different keywords like "landscape", "portrait", or an artist name
          </p>
        </div>
      )}

      {/* Initial State - Popular Searches (not for World Illustrated, which has always-visible category buttons) */}
      {!searched && results.length === 0 && activeSource !== 'world-illustrated' && (
        <div className="text-center py-8">
          <div className="bg-slate-50 rounded-lg p-6 max-w-md mx-auto">
            <h4 className="font-medium text-slate-700 mb-2">Popular Searches</h4>
            <div className="flex flex-wrap gap-2 justify-center">
              {getQuickSearchTerms().map((term) => (
                <button
                  key={term}
                  onClick={() => handleQuickSearch(term)}
                  className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm text-slate-600 transition-colors"
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = `${brand.colors.primary}10`;
                    e.target.style.borderColor = `${brand.colors.primary}60`;
                    e.target.style.color = brand.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '';
                    e.target.style.borderColor = '';
                    e.target.style.color = '';
                  }}
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4 flex items-center justify-center gap-1">
            <ExternalLink size={12} />
            {getAttributionText()}
          </p>
        </div>
      )}

      {/* World Illustrated Initial State - Just show attribution */}
      {!searched && results.length === 0 && activeSource === 'world-illustrated' && (
        <div className="text-center py-4">
          <p className="text-xs text-slate-400 flex items-center justify-center gap-1">
            <ExternalLink size={12} />
            {getAttributionText()}
          </p>
        </div>
      )}
    </div>
  );
};

export default LibraryPicker;
